import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { apiClient, extractApiError } from '../api/axios';
import { API } from '../constants/api';
import { DEFAULT_PAGE_SIZE } from '../constants/ui';

const initialState = {
  couriers: null,
  loading: false,
  error: null,
  success: false,
  editSuccess: false,
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  total: null,
  courierId: null,
  courierModal: false,
};

export const fetchCouirers = createAsyncThunk('couriers/fetchCouirers', async (filter, { rejectWithValue }) => {
  try {
    const response = await apiClient.get(API.couriers.statisticsAll, { params: filter });
    return response.data;
  } catch (error) {
    return rejectWithValue(extractApiError(error));
  }
});

export const createCourier = createAsyncThunk('couriers/createCouirer', async (data, { rejectWithValue }) => {
  try {
    const response = await apiClient.post(API.couriers.list, data);
    return response.data;
  } catch (error) {
    return rejectWithValue(
      error?.response?.data?.phone_number || error?.response?.data?.password || extractApiError(error)
    );
  }
});

export const updateCourier = createAsyncThunk(
  'couriers/updateCourier',
  async ({ id, courierData }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(API.couriers.byId(id), courierData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.phone_number || error?.response?.data?.password || extractApiError(error)
      );
    }
  }
);

export const deleteCourier = createAsyncThunk('couriers/deleteCourier', async (id, { rejectWithValue }) => {
  try {
    const response = await apiClient.del(API.couriers.byId(id));
    return response.data;
  } catch (error) {
    return rejectWithValue(extractApiError(error) || 'Failed to delete courier');
  }
});

export const couriersSlice = createSlice({
  name: 'couriers',
  initialState,
  extraReducers: (builder) => {
    builder.addCase(fetchCouirers.pending, (state) => {
      state.loading = true;
      state.error = '';
      state.success = false;
    });
    builder.addCase(fetchCouirers.fulfilled, (state, action) => {
      state.loading = false;
      state.page = action.payload.page;
      state.pageSize = action.payload.page_size;
      state.total = action.payload.total;
      state.couriers = action.payload.statistics || action.payload.results || [];
      state.success = true;
    });
    builder.addCase(fetchCouirers.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || 'Statistika yuklashda xatolik yuz berdi';
      state.success = false;
      toast.error(action.payload || 'Statistika yuklashda xatolik yuz berdi');
    });

    builder
      .addCase(createCourier.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.editSuccess = false;
      })
      .addCase(createCourier.fulfilled, (state, action) => {
        state.loading = false;
        state.couriers = [action.payload, ...(state.couriers || [])];
        state.editSuccess = true;
        toast.success('Kuryer muvaffaqiyatli yaratildi!');
      })
      .addCase(createCourier.rejected, (state, action) => {
        state.loading = false;
        state.editSuccess = false;
        state.error = action.payload;
        toast.error(action.payload);
      });

    builder
      .addCase(updateCourier.pending, (state) => {
        state.loading = true;
        state.editSuccess = false;
        state.error = null;
      })
      .addCase(updateCourier.fulfilled, (state, action) => {
        state.loading = false;
        if (state.couriers && Array.isArray(state.couriers)) {
          const updatedCourierIndex = state.couriers.findIndex((courier) => courier.id === action.payload.id);
          if (updatedCourierIndex !== -1) {
            state.couriers[updatedCourierIndex] = action.payload;
          } else {
            state.couriers = [action.payload, ...state.couriers];
          }
        } else {
          state.couriers = [action.payload];
        }
        state.editSuccess = true;
        toast.success('Kuryer muvaffaqiyatli yangilandi!');
      })
      .addCase(updateCourier.rejected, (state, action) => {
        state.loading = false;
        state.editSuccess = false;
        state.error = action.payload;
        toast.error(action.payload || 'Kuryerni yangilashda xato!');
      });

    builder
      .addCase(deleteCourier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCourier.fulfilled, (state, action) => {
        state.loading = false;
        const deletedId = action.meta.arg;
        state.couriers = state.couriers ? state.couriers.filter((courier) => courier.id !== deletedId) : [];
        toast.success("Kuryer muvaffaqiyatli o'chirildi");
      })
      .addCase(deleteCourier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        toast.error(action.payload || "Kuryerni o'chirishda xatolik yuz berdi");
      });
  },
  reducers: {
    setPage: (state, { payload }) => {
      state.page = payload;
    },
    setCourierId: (state, { payload }) => {
      state.courierId = payload;
    },
    setCourierModal: (state, { payload }) => {
      state.courierModal = payload;
    },
  },
});

export const { setPage, setCourierModal, setCourierId } = couriersSlice.actions;

export default couriersSlice.reducer;
